import React from 'react';
import { ChevronDown } from 'lucide-react';
import { useLocation } from 'react-router-dom';

const Policy = ({ id, title, children }) => (
  <details id={id} className="group border-b border-gray-100 py-3" data-testid={`policy-${id}`}>
    <summary className="flex items-center justify-between cursor-pointer list-none text-sm font-normal text-gray-500 hover:text-gray-700 transition-colors">
      <span>{title}</span>
      <ChevronDown size={16} className="text-gray-400 transition-transform duration-200 group-open:rotate-180" />
    </summary>
    <div className="mt-3 space-y-3 text-xs leading-relaxed font-light text-gray-400">
      {children}
    </div>
  </details>
);

const P = ({ children }) => <p>{children}</p>;
const H = ({ children }) => <p className="font-medium text-gray-500 mt-3">{children}</p>;

export const LegalPolicies = () => {
  const location = useLocation();
  React.useEffect(() => {
    const hash = (location.hash || '').replace('#', '');
    if (!hash) return;
    const el = document.getElementById(hash);
    if (el && el.tagName === 'DETAILS') {
      el.open = true;
      setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150);
    }
  }, [location]);

  return (
  <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mt-16 mb-10" data-testid="legal-policies">
    <p className="text-xs uppercase tracking-[0.2em] text-gray-300 mb-2">Legal</p>
    <div className="rounded-xl border border-gray-100 bg-gray-50/40 px-5 py-2">
      <Policy id="privacy" title="Privacy Policy">
        <P>Last Updated: June 2026</P>
        <P>Rezvani Furniture Studio ("Rezvani," "we," "us," or "our") respects your privacy. This Privacy Policy explains how we collect, use, and protect your information when you visit rezvanifurniturestudio.com (the "Site") or make a purchase.</P>
        <H>1. Information We Collect</H>
        <P>Information you provide: name, shipping/billing address, email, phone number, and order details when you place an order, request a quote, submit a dealer inquiry, contact us, or subscribe to our newsletter. Payment information: card payments are processed securely by our payment processor, Stripe. We do not store your full card number on our servers. Automatically collected: we use Google Analytics and similar tools to collect device, browser, page-view, and usage data via cookies to understand and improve our Site.</P>
        <H>2. How We Use Your Information</H>
        <P>To process and fulfill orders; respond to inquiries and dealer requests; send order confirmations and customer-support communications; send marketing emails (only if you opt in); improve our Site and products; and prevent fraud and comply with the law.</P>
        <H>3. Sharing of Information</H>
        <P>We do not sell your personal information. We share it only with service providers who help us operate (e.g., Stripe for payments, shipping carriers, email/analytics providers) and when required by law. These providers are limited to the data needed to perform their services.</P>
        <H>4. Cookies & Analytics</H>
        <P>We use cookies and Google Analytics to measure traffic and improve the experience. You can disable cookies in your browser, though some features may not work properly.</P>
        <H>5. Data Security</H>
        <P>We use industry-standard measures to protect your information. No method of transmission over the Internet is 100% secure, and we cannot guarantee absolute security.</P>
        <H>6. Your Rights</H>
        <P>You may request access to, correction of, or deletion of your personal information, and you may unsubscribe from marketing emails at any time using the link in our emails or by contacting us.</P>
        <H>7. Children's Privacy</H>
        <P>Our Site is not directed to children under 13, and we do not knowingly collect their information.</P>
        <H>8. Changes to This Policy</H>
        <P>We may update this Policy from time to time. Changes take effect when posted, with an updated "Last Updated" date.</P>
        <H>9. Contact Us</H>
        <P>Rezvani Furniture Studio — Chandler, AZ. Email: rezvanifurniturestudio@gmail.com • Phone: (623) 888-8644.</P>
      </Policy>

      <Policy id="terms" title="Terms of Service">
        <P>Last Updated: June 2026</P>
        <P>These Terms of Service ("Terms") govern your use of rezvanifurniturestudio.com and any purchase you make. By using the Site or placing an order, you agree to these Terms.</P>
        <H>1. Products & Pricing</H>
        <P>We sell Tenjam SPLASH Series in-pool furniture and accessories. We strive for accuracy in product descriptions, images, and pricing, but errors may occur. We reserve the right to correct errors and to change prices at any time. Custom-color and made-to-order items are produced to your specifications.</P>
        <H>2. Orders & Payment</H>
        <P>All orders are subject to acceptance and availability. Payment is processed securely through Stripe. By submitting an order, you authorize us to charge your payment method for the total amount, including applicable taxes and shipping.</P>
        <H>3. Shipping</H>
        <P>We ship within the contiguous United States. We currently do not ship to Alaska or Hawaii. Delivery times are estimates and not guaranteed. Risk of loss passes to you upon delivery to the carrier.</P>
        <H>4. Returns & Refunds</H>
        <P>Purchases are governed by our Return & Refund Policy, including a 30-day return window and specific conditions for damaged or defective items. Please review that policy for full details.</P>
        <H>5. Warranty</H>
        <P>Tenjam SPLASH Series products carry a manufacturer's limited warranty against manufacturing and material defects. Warranty does not cover damage from misuse, neglect, improper installation, or unauthorized modification.</P>
        <H>6. Acceptable Use</H>
        <P>You agree not to use the Site for any unlawful purpose, to infringe our intellectual property, or to interfere with the Site's operation. All content, logos, and images are the property of Rezvani Furniture Studio or its licensors.</P>
        <H>7. Limitation of Liability</H>
        <P>To the fullest extent permitted by law, Rezvani Furniture Studio is not liable for indirect, incidental, or consequential damages arising from your use of the Site or products. Our total liability for any claim shall not exceed the amount you paid for the product at issue.</P>
        <H>8. Indemnification</H>
        <P>You agree to indemnify and hold harmless Rezvani Furniture Studio from claims arising out of your misuse of the Site or violation of these Terms.</P>
        <H>9. Governing Law</H>
        <P>These Terms are governed by the laws of the State of Arizona, without regard to conflict-of-law principles. Any disputes shall be resolved in the state or federal courts located in Arizona.</P>
        <H>10. Changes to These Terms</H>
        <P>We may update these Terms at any time. Continued use of the Site after changes constitutes acceptance.</P>
        <H>11. Contact Us</H>
        <P>Rezvani Furniture Studio — Chandler, AZ. Email: rezvanifurniturestudio@gmail.com • Phone: (623) 888-8644.</P>
      </Policy>
    </div>
  </section>
  );
};

export default LegalPolicies;
